<i18n>
{
  "fr": {
    "zoneInputImage": {
      "placeholder": "Importer une image",
      "placeholderDebug": "Ajouter une image ou générer par IA",
      "qualityWarning": "Votre fichier semble de qualité insuffisante et risque d'être pixelisé à l'impression."
    }
  }
}
</i18n>

<template>
  <BaseInputContent
    :class="[
      BLOCK_SELECTOR,
      {
        [`${BLOCK_SELECTOR}--is-empty`]: !hasContent,
        [`${BLOCK_SELECTOR}--debug-ai-gen`]: isDebugAiGenEnabled,
      },
    ]"
    v-bind="{
      allowEdit: customProduct && hasContent,
      allowDelete: customProduct && hasContent,
    }"
    @edit="toggleEditModal"
    @delete="onDelete"
    @prepend-click="toggleEditModal"
  >
    <template #inputPrepend>
      <img v-if="url" class="zone-input-image__preview-img" :src="url" alt="" />

      <div v-else class="zone-input-image__preview-placeholder"></div>
    </template>

    <template #main>
      <input
        :id="name"
        type="file"
        accept="image/*"
        class="zone-input-image__input"
        @change="onChange"
        @click="onClick"
      />
      <label :for="name" class="zone-input-image__label" @click="onClick">
        <output
          v-if="fileName"
          :for="name"
          class="zone-input-image__label-output"
          v-text="fileName"
        ></output>

        <template v-else>
          {{
            $t(
              isDebugAiGenEnabled
                ? 'zoneInputImage.placeholderDebug'
                : 'zoneInputImage.placeholder'
            )
          }}
        </template>

        <ConfiguratorLayerEditorModal
          v-if="customization"
          v-bind="{
            group,
            inputImageUrl: url,
            fill:
              (isActiveScreenPrintingColor &&
                activeScreenPrintingColor &&
                activeScreenPrintingColor.value) ||
              undefined,
            customProduct,
            screenPrinting: isActiveScreenPrintingColor,
          }"
          :active.sync="isModalActive"
          :customization.sync="computedCustomization"
          :file.sync="file"
        />
      </label>
    </template>

    <template v-if="customization.badQuality" #after>
      <div class="zone-input-image__warning-block">
        <span class="zone-input-image__warning-icon"></span>
        {{ $t('zoneInputImage.qualityWarning') }}
      </div>
    </template>
  </BaseInputContent>
</template>

<script>
import BaseInputContent from './BaseInputContent';
import ConfiguratorLayerEditorModal from '../Modals/ConfiguratorLayerEditorModal';
import {
  ConfiguratorGetters,
  ConfiguratorMutation,
  mapGetters as mapConfiguratorGetters,
  prefixType as prefixConfiguratorType,
} from '../../../store/modules/configurator';
import ZoneInputEditorModal from '../../../mixins/ZoneInputEditorModal';
import { mmToPx, Resolution } from '../../../utils/MathUtils';
import { loadImageAndCache } from '../../../utils/ImageUtils';

export default {
  name: 'ZoneInputImage',
  components: { ConfiguratorLayerEditorModal, BaseInputContent },
  mixins: [ZoneInputEditorModal],
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: undefined,
    },
    customProduct: Boolean,
  },
  data() {
    return {
      /** @type {File|undefined} */
      file: undefined,
      BLOCK_SELECTOR: 'zone-input-image',
      localValue: undefined,
      isModalActive: false,
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      isActiveScreenPrintingColor:
        ConfiguratorGetters.IS_ACTIVE_PRODUCT_VARIANT_SCREEN_PRINTING,
      activeScreenPrintingColor:
        ConfiguratorGetters.GET_ACTIVE_SCREEN_PRINTING_COLOR,
    }),
    fileName() {
      return (
        (this.file && this.file.name) ||
        (this.localValue &&
          this.localValue
            .split('/')
            .pop()
            .split('?') // Remove query string
            .shift()) ||
        undefined
      );
    },
    url() {
      return (
        (this.file && URL.createObjectURL(this.file)) ||
        this.localValue ||
        undefined
      );
    },
    computedCustomization: {
      get() {
        return this.customization || undefined;
      },
      async set(v) {
        const zone = this.group.zoneMap[this.customization.templateZoneId];
        const customization = {
          ...v,
          // Ensure id, type and templateZoneId have not been updated
          id: this.customization.id,
          type: this.customization.type,
          templateZoneId: zone.id,
          url: this.url,
          badQuality: await this.getBadQualityFlag(this.url, v),
        };

        this.$store.commit(
          prefixConfiguratorType(ConfiguratorMutation.SET_ZONE_CUSTOMIZATION),
          {
            groupId: zone.groupId === null ? undefined : zone.groupId,
            customization,
          }
        );
      },
    },
    zone() {
      return this.group.zoneMap[this.customization.templateZoneId];
    },
    hasContent: {
      get() {
        return this.value;
      },
    },
    isDebugAiGenEnabled() {
      return new URLSearchParams(globalThis.location.search).has('debugAiGen');
    },
  },
  watch: {
    value: {
      handler(value) {
        this.localValue = value || undefined;
      },
      immediate: true,
    },
    computedCustomization(customization) {
      if (customization.url) {
        this.localValue = customization.url;
      }
    },
    isModalActive(isModalACtive) {
      this.$emit(isModalACtive ? 'focus' : 'blur');
    },
  },
  methods: {
    async getBadQualityFlag(fileUrl, customization) {
      let target;

      if (this.customProduct) {
        target = {
          width: customization.width * customization.scaleX,
          height: customization.height * customization.scaleY,
        };
      } else {
        target = this.zone;
      }

      return this.validateFileSize(
        await loadImageAndCache(fileUrl),
        this.dimensionsToPx(target)
      );
    },
    validateFileSize(
      { width, height },
      { width: targetWidth, height: targetHeight }
    ) {
      return width < targetWidth || height < targetHeight;
    },
    /**
     *
     * @param {number} width
     * @param {number} height
     * @param {number} resolution
     * @return {{width: number, height: number}}
     */
    dimensionsToPx({ width, height }, resolution = Resolution.PRINT) {
      return {
        width: mmToPx(width, resolution),
        height: mmToPx(height, resolution),
      };
    },
    async onChange(e) {
      this.file = e.target.files[0];

      if (!this.customProduct) {
        const objectUrl = URL.createObjectURL(this.file);
        const zone = this.group.zoneMap[this.customization.templateZoneId];
        const customization = {
          ...this.customization,
          url: objectUrl,
          badQuality: await this.getBadQualityFlag(objectUrl),
        };

        this.$store.commit(
          prefixConfiguratorType(ConfiguratorMutation.SET_ZONE_CUSTOMIZATION),
          {
            groupId: zone.groupId === null ? undefined : zone.groupId,
            customization,
          }
        );

        return;
      }

      this.$emit('input', this.file);
    },
    toggleEditModal(e) {
      if (e) {
        e.preventDefault();
      }

      this.isModalActive = true;
    },
    onClick(e) {
      e.preventDefault();

      this.isModalActive = true;
    },
    onDelete() {
      this.$store.commit(
        prefixConfiguratorType(ConfiguratorMutation.DELETE_ZONE_CUSTOMIZATION),
        this.customization.id
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.zone-input-image {
  $block-selector: &;

  &::v-deep .base-input-content__body {
    border-radius: 0.75rem;
    border: 1px solid $color-arapawa;
    padding-left: 0.5rem;
  }

  &--is-empty {
    &::v-deep .base-input-content__body {
      padding-left: 0;
      border: 1px dashed $color-arapawa;
    }

    &::v-deep .base-input-content__input-wrapper {
      flex: unset;
    }

    &::v-deep .base-input-content__input-group {
      justify-content: center;
    }
  }

  &--is-empty#{$block-selector}--debug-ai-gen {
    &::v-deep .base-input-content__body {
      background: linear-gradient(white, white) padding-box,
        conic-gradient(
            from var(--rainbow-angle, 0deg),
            #ff0000,
            #ff7700,
            #ffff00,
            #00ff00,
            #0000ff,
            #8b00ff,
            #ff0000
          )
          border-box;
      border: 2px solid transparent;
      animation: rainbow-border 3s linear infinite;
    }
  }

  &__input {
    @include sr-only();
  }

  &__label {
    flex: 1;
    min-width: 0;
    cursor: pointer;
    padding: 1rem 0.3125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $color-arapawa;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
    }

    #{$block-selector}__input:active + & {
      cursor: wait; // Set wait cursor while browser is opening the file picker
    }

    #{$block-selector}--is-empty & {
      text-align: center;
      text-transform: uppercase;
      font-weight: 600;
      width: fit-content;
      padding: 0;
      margin: auto 0;
    }
  }

  &__label-output {
    display: block;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &::v-deep .base-input-content {
    &__input-group {
      position: relative;
    }

    &__input-group-prepend {
      position: relative;
    }
  }

  &__preview-placeholder {
    color: $color-arapawa;

    &::after {
      @extend %icon;
      @extend %icon--add-photo;

      display: block;
      font-size: 1.5em;
    }
  }

  &--debug-ai-gen &__preview-placeholder {
    &::after {
      @extend %icon--customization;
    }
  }

  &__preview-img {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }

  &__warning-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    width: 1.5em;
    height: 1.5em;
    padding: 0.25em;
    color: $color-orange-flamingo;
    margin: 0 2px;
    border-radius: 50%;
    border: 2px solid $color-orange-flamingo;
    align-self: center;
    flex-shrink: 0;

    &::before {
      content: '!';
    }
  }

  &__warning-block {
    color: $color-orange-flamingo;
    background-color: rgba($color-orange-flamingo, 0.1);
    border-radius: 0.75rem;
    padding: 0.625rem;
    margin-top: 0.625rem;
    font-size: 0.875rem;
    font-weight: 600;
    display: flex;
    gap: 0.25rem;
  }
}

@property --rainbow-angle {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}

@keyframes rainbow-border {
  from {
    --rainbow-angle: 0deg;
  }

  to {
    --rainbow-angle: 360deg;
  }
}
</style>
